/**
 * Login Component
 * Implements authentication flow for the Deceptive Design Agent application
 * Design: Based on Figma design with two-column layout featuring form and decorative background
 * 
 * Features:
 * - Firebase Firestore authentication
 * - Email/password validation
 * - User role and domain management
 * - Responsive design with mobile support
 * - Loading states and error handling
 */

import React, { useState } from 'react';
import { loginUser } from '../services/teamService';
import { getUserDomains } from '../services/firebaseService';
import { UserSession } from '../types';
import styles from './Login.module.css';
import logoSvg from '../assets/logo.svg';
import rightContainerImage from '../assets/login_page_right_container.jpg';

interface LoginProps {
  onLoginSuccess: (userSession: UserSession) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  // State management for form inputs and UI states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles the login form submission
   * Uses simplified teamService that checks both admin and team members
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use the new simplified login service
      const loginResult = await loginUser(email, password);

      if (!loginResult) {
        setError('Invalid email or password');
        setIsLoading(false);
        return;
      }

      // Load existing domains for this user (from admin or team member doc)
      const domains = await getUserDomains(loginResult.email);

      // Create user session
      const userSession: UserSession = {
        userId: loginResult.userId,
        email: loginResult.email,
        name: loginResult.name,
        tag: loginResult.tag,
        adminId: loginResult.adminId,
        isAdmin: loginResult.isAdmin,
        adminEmail: loginResult.adminEmail,
        domains,
      };

      // Persist user session data in localStorage
      localStorage.setItem('userSession', JSON.stringify(userSession));
      localStorage.setItem('userEmail', email); // For backward compatibility
      localStorage.setItem('adminId', loginResult.adminId); // Important for subcollection queries
      localStorage.setItem('userDomains', JSON.stringify(domains));
      localStorage.setItem('access_token', loginResult.accessToken ?? '');
      localStorage.setItem('refresh_token', loginResult.refreshToken ?? '');
      localStorage.setItem('organization_name', loginResult.organizationName ?? '');
      localStorage.setItem('user_id', loginResult.userId ?? '');
      localStorage.setItem('org_id', loginResult.orgId ?? '');

      // Trigger success callback to update parent component
      onLoginSuccess(userSession);
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      {/* Left column: Logo and login form */}
      <div className={styles.leftColumn}>
        {/* FairPatterns logo */}
        <div className={styles.logoContainer}>
          <img src={logoSvg} alt="FairPatterns by Amurabi" />
        </div>

        {/* Form content wrapper - centers form vertically */}
        <div className={styles.contentWrapper}>
          <div className={styles.formContainer}>
            {/* Header section with title and subtitle */}
            <div className={styles.textSection}>
              <h1 className={styles.title}>Deceptive Design Agent</h1>
              <p className={styles.subtitle}>Please login to continue to your account.</p>
            </div>

            {/* Error message display */}
            {error && (
              <div className={styles.errorAlert}>
                <span>{error}</span>
                <button
                  className={styles.closeButton}
                  onClick={() => setError('')}
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}

            {/* Login form */}
            <form onSubmit={handleLogin} style={{ width: '100%' }}>
              {/* Email input field */}
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.formLabel}>
                  Email
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="email"
                    type="email"
                    className={styles.formInput}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password input field */}
              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.formLabel}>
                  Password
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="password"
                    type="password"
                    className={styles.formInput}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {/* Submit button with loading state */}
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className={styles.spinner} />
                    <span>Signing in...</span>
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Right column: Decorative background with image */}
      <div className={styles.rightColumn}>
        <div className={styles.decorativeContainer}>
          <img src={rightContainerImage} alt="" className={styles.decorativeImage} />
        </div>
      </div>
    </div>
  );
};

export default Login;

